/**
 * ClubLayout — MindEscapes® brand shell wrapper for the /club portal.
 *
 * - Loads Fraunces + Archivo from Google Fonts (idempotent).
 * - Renders the header band (MindEscapes monogram + member avatar) and footer.
 * - Children render inside `.club-shell` so all isolated CSS tokens apply.
 *
 * FIR-20260611-001
 */

import React, { useEffect } from "react";
import "@/styles/club-mindescapes.css";

const FONTS_HREF = "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400;1,9..144,500&family=Archivo:wght@400;500;600;700&display=swap";

function ensureFonts() {
  if (typeof document === "undefined") return;
  if (document.getElementById("club-mindescapes-fonts")) return;
  const link = document.createElement("link");
  link.id = "club-mindescapes-fonts";
  link.rel = "stylesheet";
  link.href = FONTS_HREF;
  document.head.appendChild(link);
  // Preconnect
  const pc1 = document.createElement("link");
  pc1.rel = "preconnect";
  pc1.href = "https://fonts.googleapis.com";
  document.head.appendChild(pc1);
  const pc2 = document.createElement("link");
  pc2.rel = "preconnect";
  pc2.href = "https://fonts.gstatic.com";
  pc2.crossOrigin = "";
  document.head.appendChild(pc2);
}

export default function ClubLayout({ member, children, onSignOut }) {
  useEffect(() => { ensureFonts(); }, []);

  const initial = (member?.first?.[0] || "M").toUpperCase();
  const nameLine = member ? `${member.first} ${member.last}`.trim() : "Welcome";

  return (
    <div className="club-shell" data-testid="club-shell">
      <div className="grain" />
      <header className="club-header">
        <div className="wrap bar">
          <div className="brand" data-testid="club-brand">
            <img
              src="https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/partners/mindescapes-logo.png"
              alt="MindEscapes"
              className="me-logo"
              data-testid="club-brand-logo"
            />
            <div className="bt">
              <small>
                <a href="https://mindescapes.in" target="_blank" rel="noopener noreferrer"
                   data-testid="club-header-main-site"
                   style={{ color: "inherit", textDecoration: "none" }}>
                  Club Discovery Network · mindescapes.in ↗
                </a>
              </small>
              <b className="serif">MindEscapes<sup>®</sup></b>
            </div>
          </div>
          {member && (
            <div className="welcome" data-testid="club-welcome" style={{ alignItems: "center" }}>
              <div className="w-txt">
                <small>Signed in as</small>
                <b>{nameLine}</b>
              </div>
              <div className="avatar serif">{initial}</div>
              {onSignOut && (
                <button
                  type="button"
                  onClick={onSignOut}
                  data-testid="club-signout-btn"
                  style={{
                    marginLeft: 12, background: "transparent",
                    border: "1px solid var(--line)", borderRadius: 100,
                    padding: "6px 14px", fontSize: 11.5, fontWeight: 600,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    color: "var(--muted)", cursor: "pointer",
                  }}>
                  Sign out
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {children}

      <footer className="club-footer">
        <div className="wrap foot" style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)",
          gap: 28, alignItems: "flex-start",
        }}>
          {/* Brand block ─────────────────────────────────────────── */}
          <div>
            <b className="serif" style={{ fontSize: 18 }}>MindEscapes<sup>®</sup></b>
            <p style={{ marginTop: 8 }}>
              The Club Discovery Network for MindEscapes<sup>®</sup> members.
              Family-only access. Concierge-arranged.
            </p>
            <p style={{ marginTop: 10 }}>
              <a href="https://mindescapes.in" target="_blank" rel="noopener noreferrer"
                 data-testid="club-footer-main-site"
                 style={{ color: "var(--teal-d)", fontWeight: 600,
                          textDecoration: "underline", textUnderlineOffset: 3 }}>
                mindescapes.in ↗
              </a>
            </p>
            {/* FIR-20260624-RulesLink · founder direction: link to the
                authoritative Terms / Club Rules / Privacy Policy PDF
                published on the main site. */}
            <p style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.5 }}>
              Read the full{" "}
              <a href="https://mindescapes.in/files/others/MEClubMembershipRules.pdf"
                 target="_blank" rel="noopener noreferrer"
                 data-testid="club-footer-rules-pdf"
                 style={{ color: "var(--teal-d)", fontWeight: 600,
                          textDecoration: "underline", textUnderlineOffset: 3 }}>
                Terms, Club Rules and Privacy Policy ↗
              </a>
            </p>
          </div>

          {/* Contact Us ──────────────────────────────────────────── */}
          <div data-testid="club-footer-contact">
            <small style={{ fontSize: 11, letterSpacing: "0.13em",
                            textTransform: "uppercase", fontWeight: 700,
                            color: "var(--muted)" }}>Contact Us</small>
            <div style={{ marginTop: 8, lineHeight: 1.55, fontSize: 13.5 }}>
              MindEscapes<sup>®</sup>, Nilgiris<br />
              7/7 B4A Yellanalli Mynala Road<br />
              Yellanalli, Ketti Panchayat<br />
              Nilgiris 643243<br />
              <a href="tel:+919489052222" style={{ color: "var(--teal-d)" }}>+91 94890 52222</a><br />
              <a href="mailto:enquiries@mindescapes.in" style={{ color: "var(--teal-d)" }}>enquiries@mindescapes.in</a>
            </div>
          </div>

          {/* Registered Office ───────────────────────────────────── */}
          <div data-testid="club-footer-regoffice">
            <small style={{ fontSize: 11, letterSpacing: "0.13em",
                            textTransform: "uppercase", fontWeight: 700,
                            color: "var(--muted)" }}>Registered Office</small>
            <div style={{ marginTop: 8, lineHeight: 1.55, fontSize: 13.5 }}>
              #83, 3rd Floor, 7th Cross<br />
              4th B Block, Koramangala<br />
              Bangalore-560034<br />
              India
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
